import { useState, useEffect } from 'react';

interface HeaderProps {
  isScrolled: boolean;
}

export default function Header({ isScrolled }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    const targetId = href.replace('#', '');
    const targetElement = document.getElementById(targetId);
    
    if (targetElement) {
      const headerOffset = 80;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // メニューが開いている時はスクロールを無効化
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { label: 'デジポップブーストとは', href: '#about' },
    { label: '特徴', href: '#features' },
    { label: '支援内容', href: '#service' },
    { label: '支援の流れ', href: '#flow' },
    { label: '料金', href: '#price' },
    { label: 'よくある質問', href: '#faq' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#f5f3f0] shadow-sm' : 'bg-transparent'
        }`}
      >
        {/* 上段:サービスページへ・公式サイトへ */}
        <div
          className={`transition-all duration-300 overflow-hidden ${
            isScrolled ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-end h-8 space-x-5">
              <a
                href="https://digipop.co.jp/service/dx/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-file-list-line w-4 h-4 flex items-center justify-center text-sm"></i>
                サービスページへ
              </a>
              <a
                href="https://digipop.co.jp/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-global-line w-4 h-4 flex items-center justify-center text-sm"></i>
                公式サイトへ
              </a>
            </div>
          </div>
        </div>

        {/* 下段:ロゴ・ナビ・CTA */}
        <div className={`${!isScrolled ? 'bg-white/30 backdrop-blur-sm' : ''}`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button onClick={scrollToTop} className="cursor-pointer">
                  <img
                    src="https://static.readdy.ai/image/a52a13a88e2ccb39325c6dcb34729469/6b817571f218f2493e61dad8479b1a1c.png"
                    alt="Digipop Boost Logo"
                    className="h-8 w-auto"
                  />
                </button>
              </div>

              {/* デスクトップナビ */}
              <nav className="hidden lg:flex items-center">
                <div className="flex items-center space-x-7">
                  {navLinks.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className={`text-base font-medium transition-colors cursor-pointer ${
                        isScrolled ? 'text-gray-700 hover:text-[#9a1e23]' : 'text-white/90 hover:text-white'
                      }`}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>

                <div className="ml-8">
                  <a
                    href="https://imachika.com/viewform/?form_id=698b466b23679c3f7a84ae56"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2.5 bg-[#9a1e23] text-white text-base font-medium rounded-lg hover:bg-[#7d181c] transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow-sm hover:shadow-md"
                  >
                    <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center text-base"></i>
                    無料相談する
                  </a>
                </div>
              </nav>

              {/* モバイルハンバーガーボタン */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
                aria-label="メニューを開く"
              >
                <div className="relative w-6 h-5">
                  <span
                    className={`absolute left-0 w-6 h-0.5 transition-all duration-300 ${
                      isScrolled ? 'bg-gray-700' : 'bg-white'
                    } ${isMobileMenuOpen ? 'top-2 rotate-45' : 'top-0'}`}
                  ></span>
                  <span
                    className={`absolute left-0 top-2 w-6 h-0.5 transition-all duration-300 ${
                      isScrolled ? 'bg-gray-700' : 'bg-white'
                    } ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}
                  ></span>
                  <span
                    className={`absolute left-0 w-6 h-0.5 transition-all duration-300 ${
                      isScrolled ? 'bg-gray-700' : 'bg-white'
                    } ${isMobileMenuOpen ? 'top-2 -rotate-45' : 'top-4'}`}
                  ></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* モバイルメニューオーバーレイ */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* モバイルスライドメニュー */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-[#f5f3f0] z-50 lg:hidden transform transition-transform duration-300 ease-out shadow-xl ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* メニューヘッダー */}
          <div className="flex items-center justify-between px-6 h-16 border-b border-gray-200">
            <span className="text-base font-bold text-[#12335f]">メニュー</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="メニューを閉じる"
            >
              <i className="ri-close-line text-2xl text-gray-600"></i>
            </button>
          </div>

          {/* ナビリンク */}
          <nav className="flex-1 overflow-y-auto py-4">
            {navLinks.map((link, idx) => (
              <a
                key={idx}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block px-6 py-3.5 text-base font-medium text-gray-700 hover:bg-white/60 hover:text-[#9a1e23] transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}

            <div className="border-t border-gray-200 mt-4 pt-4">
              <a
                href="https://digipop.co.jp/service/dx/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 px-6 py-3 text-sm text-gray-600 hover:text-[#9a1e23] transition-colors cursor-pointer"
              >
                <i className="ri-file-list-line w-4 h-4 flex items-center justify-center text-sm"></i>
                サービスページへ
              </a>
              <a
                href="https://digipop.co.jp/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-2 px-6 py-3 text-sm text-gray-600 hover:text-[#9a1e23] transition-colors cursor-pointer"
              >
                <i className="ri-global-line w-4 h-4 flex items-center justify-center text-sm"></i>
                公式サイトへ
              </a>
            </div>
          </nav>

          {/* CTAボタン */}
          <div className="p-6 border-t border-gray-200">
            <a
              href="https://imachika.com/viewform/?form_id=698b466b23679c3f7a84ae56"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full px-6 py-3 bg-[#9a1e23] text-white text-base font-medium rounded-lg hover:bg-[#7d181c] transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
            >
              <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center text-base"></i>
              無料相談する
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
